<script lang="ts">
    import CalendarNotesCard from "./CalendarNotesCard.svelte";
    import CalendarPlanReportCard from "./CalendarPlanReportCard.svelte";
    import type { MyFitnessStores } from "../stores";
    import { addHours, addMinutes, startOfDay } from "date-fns";
    import { CONSTRAINT_EPSILON } from "@/Common/constants";
    import CalendarSummaryCard from "./CalendarSummaryCard.svelte";
    import { daysBetween, LocalDate, today } from "@/time";
    import { isSystemChosen, sameAvailability, usualIntensity } from "@/SetupWizards/availability";
    import { getUsualAvailability } from "@/Util/utils";
    import { getContext } from "svelte";
    import type { MedianAndWeatherStores } from "../stores";
    import DayAvailabilityPopover from "./DayAvailabilityPopover.svelte";
    import { DragType, dropzone, dragInformation } from '@/Common/DragAndDrop'
    import { moveCalendarEvent, copyCalendarEvent, importCalendarWorkout } from './CalendarEventUtils'
    import type { CalendarLoader, LoaderActivity } from "./CalendarLoaderNew";
    import { Button } from "$lib/components/ui/button/index.js";
    import { cn } from "@/lib/utils";
    import { _ } from 'svelte-i18n';
    import CalendarActivityCard from "./CalendarActivityCard.svelte";
    import { Activity } from "@/Common/ActivityFunctions";
    import CalendarUnmatchedForecastActivityCard from "./CalendarUnmatchedForecastActivityCard.svelte";

    export let myFitness: MyFitnessStores;
    export let date: Date = new Date();
    export let allActivities: LoaderActivity[];
    export let weather: any = null;
    export let calendarViewType: 'grid' | 'list';
    export let isCompactView: boolean;
    export let isTouchDevice: boolean;
    export let cal_settings;
    export let notes;
    export let planReports = null;
    export let username = null;
    export let allowSummaryCard: boolean = false
    export let forecastDay: any | null
    export let onDrag: (x: number, y: number) => void;
    export let calendarLoader: CalendarLoader;

    const { selectedTrainingPlan, dayCommentCounts } = myFitness

    //@ts-ignore
    const Xert = window.Xert;

    let root: HTMLElement;
    let displayUnits = Xert._displayUnits;

    let isLoading = false;

    $: activitiesWithCard = allActivities?.filter((la) => (
      !Activity.hasMatch(la.activity) // Ignore planned/forecast activities with a match
      && !Activity.isDuplicate(la.activity) // Ignore duplicate completed activities
      && !Activity.isHidden(la.activity) // Ignore hidden activities
      && !(Activity.isPlanned(la.activity) && new Date(la.activity.start_date) < $today) // Ignore planned activities in the past
    ))
    $: numberHiddenOrDuplicateActivities = allActivities?.filter((la) => (
        Activity.isDuplicate(la.activity) || Activity.isHidden(la.activity)
    )).length ?? 0
    $: unmatchedPastForecastActivity = date < $today ? allActivities?.find((la) => (
        Activity.isForecastEvent(la.activity)
        && !Activity.hasMatch(la.activity)
    ))?.activity : null

    $: past = date < startOfDay(new Date())

    const onCalendarAddClick = () => {
        const useRecommended = !activitiesWithCard || activitiesWithCard.length == 0

        let event = new CustomEvent("addToCalendar", {
            bubbles: true,
            detail: {
                date,
                useRecommended,
            }
        });
        selectedTrainingPlan.set(null)
        root.dispatchEvent(event);
    }

    let isMouseInCard = false
    
    function handleMouseEnter() {
        isMouseInCard = true
    }

    function handleMouseLeave() {
        isMouseInCard = false
    }

    function dispatchNotesUpdate() {
        let event = new CustomEvent("showNotesUpdate", {
            bubbles: true,
            detail: {
                date
            }
        });
        root.dispatchEvent(event);
    }

    function dispatchShowDayReview() {
        let event = new CustomEvent("showDayReview", {
            bubbles: true,
            detail: {
                date
            }
        });
        root.dispatchEvent(event);
    }

    // The availability popover keeps the settings itself; the page is told so it can check Adapt Forecast again.
    // The row of buttons is shown while it is open, which the pointer leaves the day for.
    let availabilityOpen = false
    function dispatchAvailabilitySaved() {
        root.dispatchEvent(new CustomEvent("availabilitySaved", { bubbles: true, detail: { date } }))
    }

    enum AvailabilityDotState {
        Hidden,
        DoWOverride,
        DateOverride,
    }
    const trainingMedians = getContext<MedianAndWeatherStores | undefined>('medianAndWeather')?.trainingMedians
    let availabilityDotState = AvailabilityDotState.Hidden;
    $:  {
        if (cal_settings && cal_settings.defaults && cal_settings.availabilities) {
            const _date = LocalDate.format(date)
            const weekday = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
            const default_settings = cal_settings['defaults'][weekday[date.getDay()]];
            const today_settings = cal_settings['availabilities'][_date];
            const effective = today_settings ?? default_settings
            if (today_settings && !sameAvailability(today_settings, default_settings, usualIntensity(getUsualAvailability($trainingMedians, date.getDay())))) {
                // Red: the day is not as its weekday's setting is
                availabilityDotState = AvailabilityDotState.DateOverride
            } else if (!effective.availability || !isSystemChosen(effective, usualIntensity(getUsualAvailability($trainingMedians, date.getDay())))) {
                // Yellow: the system doesn't choose everything
                availabilityDotState = AvailabilityDotState.DoWOverride
            } else {
                availabilityDotState = AvailabilityDotState.Hidden
            }
        }
    }

    // Magenta: a constraint is broken, or the user's low/high intensity mark for this day couldn't be honored (not for past days, which can no longer be scheduled).
    $: conflictDay = forecastDay && (forecastDay.d_constraint > CONSTRAINT_EPSILON || (!!forecastDay.intensity_unmet && !past))
    $: intensityUnmetTitle = forecastDay?.intensity_unmet && !past ? $_(`forecast.constraints.intensity_${forecastDay.intensity_unmet}_unmet`) : undefined

    // Comment counts for this day's thread, batch-loaded for the whole rendered range by
    // Calendar.svelte (MyFitnessStores.loadDayCommentCounts) -- transiently undefined until that
    // resolves, which just leaves the badge blank. Mirrors the activity comment badge
    // (Comments/CommentsButton.svelte): red with the unread count, else grey with the total.
    $: dayComments = $dayCommentCounts[LocalDate.format(date)]
    $: dayCommentsUnread = Xert._hasMessagingFeature ? (dayComments?.unread ?? 0) : 0
    $: dayCommentsTotal = Xert._hasMessagingFeature ? (dayComments?.total ?? 0) : 0

    let isDroppable = false
</script>

<!-- svelte-ignore a11y-no-static-element-interactions -->
<div class='cell-layout flex flex-col pb-2' title={intensityUnmetTitle} class:compact={isCompactView} class:isLoading class:cell-today={daysBetween($today, date) == 0} bind:this={root}
    class:dropDisabled={$dragInformation != null && $dragInformation.type != DragType.MOVE && $dragInformation.type != DragType.COPY && !($dragInformation.type == DragType.IMPORT && !past)}
    class:canDrop={$dragInformation != null && ($dragInformation.type == DragType.MOVE || $dragInformation.type == DragType.COPY || ($dragInformation.type == DragType.IMPORT && !past))}
    on:mouseenter={handleMouseEnter} on:mouseleave={handleMouseLeave}
    use:dropzone={{
        on_dropzone(data, operation) {
            isDroppable = false
            if (operation == 'copy') {
                const exercise = data.exercise
                const originalDate = new Date(exercise.start_date)
                const newDate = addMinutes(addHours(date, originalDate.getHours()), originalDate.getMinutes())
                copyCalendarEvent(exercise, newDate, () => calendarLoader.copyActivity(exercise, newDate), Xert)
            } else if (operation == 'move') {
                const exercise = data.exercise
                const originalDate = new Date(exercise.start_date)
                const newDate = addMinutes(addHours(date, originalDate.getHours()), originalDate.getMinutes())
                moveCalendarEvent(exercise, newDate, () => calendarLoader.moveActivity(exercise, newDate), Xert)
            } else if (operation == 'import') {
                const now = new Date()
                // const newDate = addMinutes(addHours(date, now.getHours()), now.getMinutes())
                const newDate = addMinutes(addHours(date, now.getHours()), now.getMinutes())
                importCalendarWorkout(data, newDate, () => {}, (date) =>  calendarLoader.reloadFrom(date), Xert)
                console.log('import workout', data)
            }
        },
        on_dragenter() {
            if ($dragInformation?.type == DragType.IMPORT) isDroppable = !past
            else if ($dragInformation?.type == DragType.MOVE || $dragInformation?.type == DragType.COPY) isDroppable = true;
        },
        on_dragleave() {
            isDroppable = false;
        },
    }}
>
    {#if isLoading}
        <i class="fa-regular fa-spinner-third fa-2xl fa-spin absoluteSpinner"></i>
    {/if}
    <div class='cell-header @container'>
        {#if weather && weather.success}
        <div class="@[100px]:flex hidden">
            <img aria-hidden="true" alt="weather icon" class="weather-icon" src="/assets/images/weather-{weather.weather_icon_key}.svg">
            <div class='weather-text'>
                {Math.round(parseFloat(weather.maxTemp))}&deg;
                <br>
                {Math.round(parseFloat(weather.minTemp))}&deg;
            </div>
            <img aria-hidden="true" alt="wind icon" class="@[130px]:flex hidden wind-icon" src="/assets/images/weather-wind-{weather.wind_icon_key}.svg">
            <div class='@[130px]:flex hidden wind-text'>
                {Math.round(parseFloat(weather.windSpeed))}{displayUnits == 'imperial' ? 'mph' : 'kph'}
                <br>
                {#if weather.gust !== undefined}
                    {Math.round(parseFloat(weather.gust))}{displayUnits == 'imperial' ? 'mph' : 'kph'}
                {/if}
            </div>
        </div>
        {/if}        
        <div class={cn("flex gap-1 items-center date text-center mt-[-4px] text-[18px] leading-[32px] ml-auto")}>
            {#if date >= startOfDay(new Date())} 
                <div 
                    class={cn("rounded-full w-[0.4rem] h-[0.4rem] bg-red-500 hidden", {
                        "block bg-red-500": availabilityDotState == AvailabilityDotState.DateOverride,
                        "block bg-yellow-500": availabilityDotState == AvailabilityDotState.DoWOverride
                    })} 
                />
            {/if}
            {calendarViewType == 'list' ? date.toLocaleDateString(undefined, { weekday: 'short' }) : ''}
            {date.getDate()}
        </div>
    </div>
    <div class='w-full flex flex-col gap-2'>
        {#if unmatchedPastForecastActivity && (calendarViewType == 'list' || !isCompactView)}
            <CalendarUnmatchedForecastActivityCard activity={unmatchedPastForecastActivity} layoutType={calendarViewType == 'list' ? 'horizontal' : 'vertical'} />
        {/if}
        {#if activitiesWithCard && activitiesWithCard.length > 0}
            {#if allowSummaryCard && activitiesWithCard.length > 1 && !(calendarViewType == 'grid' && isCompactView)}
                <CalendarSummaryCard loaderActivities={activitiesWithCard} {date} layoutType={calendarViewType == 'list' ? 'horizontal' : 'vertical'} {onDrag} {calendarLoader} {conflictDay} />
            {:else if activitiesWithCard.length >= 1}
                {#each {length: activitiesWithCard.length} as _, i (activitiesWithCard[i].activity.path)}
                    {#if calendarViewType == 'list' || !isCompactView}
                        <CalendarActivityCard activity={activitiesWithCard[i].activity} isStale={activitiesWithCard[i].isStale}
                          layoutType={calendarViewType == 'list' ? 'horizontal' : 'vertical'}
                          {calendarLoader} {onDrag} {conflictDay}
                        />
                    {:else if calendarViewType == 'grid'}
                        <div class={cn('w-full h-2 rounded-sm',
                          Activity.isRest(activitiesWithCard[i].activity) ? 'bg-zinc-300'
                          : Activity.isForecast(activitiesWithCard[i].activity) && conflictDay ? 'bg-[#ff00ffC0]'
                          : Activity.isForecast(activitiesWithCard[i].activity) ? 'bg-[linear-gradient(135deg,_#64dfdf_0%,_#89c2d9_100%)]'
                          : (Activity.isPlanned(activitiesWithCard[i].activity) ? 'bg-[rgba(198,170,255)]'
                          : 'bg-[rgba(108,186,102)]'))}
                        />
                    {/if}
                {/each}
            {/if}
        {/if}
        {#if intensityUnmetTitle && !(activitiesWithCard && activitiesWithCard.length > 0)}
            <!-- An unmet day is always scheduled as rest, so there is no forecast activity to color magenta. -->
            {#if calendarViewType == 'grid' && isCompactView}
                <div class='w-full h-2 rounded-sm bg-[#ff00ffC0]' title={intensityUnmetTitle} />
            {:else}
                <div class='w-full rounded-sm bg-[#ff00ffC0] px-2 py-1 text-xs text-white text-left' title={intensityUnmetTitle}>
                    {$_('forecast.constraints.intensity_unmet_short')}
                </div>
            {/if}
        {/if}
        {#if calendarViewType == 'list' || !isCompactView}
            <CalendarPlanReportCard {date} {planReports} {username} />
            <CalendarNotesCard {root} {date} {notes} />
        {/if}
    </div>
    {#if calendarViewType == 'list' || !isCompactView}
      {#if numberHiddenOrDuplicateActivities > 0}
        <!-- svelte-ignore a11y-click-events-have-key-events -->
        <!-- svelte-ignore a11y-missing-attribute -->
        <a class="text-xs text-zinc-500 mt-0.5" on:click={dispatchShowDayReview}>
          + {numberHiddenOrDuplicateActivities} hidden {numberHiddenOrDuplicateActivities == 1 ? 'activity' : 'activities'}
        </a>
      {/if}
      {#if isDroppable}
          <div class='h-[28px] mt-2 w-full'>
              <div class='w-full h-[6px] bg-xertlime rounded-full'/>
          </div>
      {:else if isMouseInCard || isTouchDevice || availabilityOpen}
          <div class='flex-1' />
          <div class="flex flex-row gap-2 mt-2">
              <Button variant='outline' title="Add an Activity" class='h-[28px] min-w-0 px-0 flex grow items-center content-center border-zinc-400' on:click={onCalendarAddClick}>
                  <i class='fa-thin fa-lg fa-circle-plus'/>
              </Button>
              <Button variant='outline'
                title={dayCommentsUnread > 0 ? `${dayCommentsUnread} unread comment${dayCommentsUnread == 1 ? '' : 's'}` : "Add a Note, Comment, or Schedule an Update"}
                class='relative h-[28px] min-w-0 px-0 flex grow items-center content-center border-zinc-400' on:click={dispatchNotesUpdate}>
                  <i class="relative fa-thin fa-lg fa-calendar-lines-pen"/>
                  <!-- Sibling of the icon rather than nested inside it (as the availability dot is
                       above) -- this one has text in it, which would otherwise render in the Font
                       Awesome face the <i> sets. -->
                  {#if dayCommentsUnread > 0}
                      <span class="absolute -top-1 -right-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-medium text-destructive-foreground">
                          {dayCommentsUnread}
                      </span>
                  {:else if dayCommentsTotal > 0}
                      <span class="absolute -top-1 -right-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-muted px-1 text-[10px] font-medium text-muted-foreground border border-border">
                          {dayCommentsTotal}
                      </span>
                  {/if}
              </Button>
              {#if date >= startOfDay(new Date())}
                  <DayAvailabilityPopover {date} showScope asModal bind:open={availabilityOpen} onSaved={dispatchAvailabilitySaved} let:builder>
                      <Button builders={[builder]} variant='outline' title="Set Availability" class='h-[28px] min-w-0 px-0 flex grow items-center content-center border-zinc-400'>
                          <i class='relative fa-thin fa-lg fa-clock'>
                              <div 
                                  class={cn("rounded-full w-2 h-2 absolute top-[-3px] right-[-3px] hidden", {
                                      "block bg-red-500": availabilityDotState == AvailabilityDotState.DateOverride,
                                      "block bg-yellow-500": availabilityDotState == AvailabilityDotState.DoWOverride
                                  })} 
                              />
                          </i>
                      </Button>
                  </DayAvailabilityPopover>
              {:else}
                  <Button variant='outline' title="Review the Day's Activities" class='h-[28px] min-w-0 px-0 flex grow items-center content-center border-zinc-400' on:click={dispatchShowDayReview}>
                      <i class="relative fa-thin fa-lg fa-magnifying-glass"/>
                  </Button>
              {/if}
          </div>
      {:else if !isMouseInCard && !availabilityOpen}
          <!-- The button row above is hover-only, so a day with comments would otherwise look empty
               until you hover it. Sits in the same slot the notes button occupies on hover (hence
               the matching flex-1 push), just as a faint hint -- the count itself is on the badge. -->
          <div class='flex-1' />
          <div class='h-[28px] mt-2 flex items-center'>
              {#if dayCommentsTotal > 0}
                  <i class={cn('fa-thin fa-lg fa-messages', dayCommentsUnread > 0 ? 'text-destructive' : 'text-zinc-400')}
                    title={dayCommentsUnread > 0
                      ? `${dayCommentsUnread} unread comment${dayCommentsUnread == 1 ? '' : 's'}`
                      : `${dayCommentsTotal} comment${dayCommentsTotal == 1 ? '' : 's'}`}
                  />
              {/if}
          </div>
      {/if}
    {/if}
</div>

<style lang="scss">
    .cell-layout {
        height: 100%;
        width: 100%;
        padding: 10px 8px 0px;
        border: 1px solid #ECECEC;

        &.isLoading {
            pointer-events: none;
            filter: brightness(0.75);
            background-color: white;
        }
        &.compact {
            padding: 10px 4px 0px;
        }
        &.cell-today {
            background-color: #F5FEFF;
            border-top: 4px solid #016e79;
            padding-top: 6px;
        }
    }
    .cell-header {
        display: flex;
        flex-direction: row;
        font-weight: 400;
        text-align: right;
    }

    .absoluteSpinner {
        position: absolute;
        top: 50%;
        left: calc(50% - 16px);
        transform: translate(-50%, -50%);
        color: #016e79;
        z-index: 1;
    }

    .weather-text {
        font-weight: 400;
        font-size: 10px;
        justify-content: center;
        max-width: 18px;
        margin-right: 8px;
    }

    .wind-text {
        font-weight: 400;
        font-size: 10px;
        justify-content: center;
        max-width: 28px;
    }

    .weather-icon {
        width: 20px;
        height: 20px;
        margin: 4px 4px 0px 0px;
    }

    .wind-icon {
        width: 14px;
        height: 14px;
        margin: 6px 4px 0px 0px;
    }

    .cell-layout:global(.droppable) * {pointer-events: none !important;}

    .dropDisabled {pointer-events: none;}
</style>
